/**
 * Organisation-scoped links.
 *
 * Boards, chats and documents are scoped to the ACTIVE organisation, so a link
 * to one opened while another organisation is active 404s. A link can carry
 * `?org=<organisationId>`; `LoginProtectedRoute` then switches a member to that
 * organisation before the page loads. The parameter grants nothing: the switch
 * is refused for a non-member (client-side and by Keycloak), and the backend
 * still scopes every request to the token's active organisation.
 */
export const ORG_PARAM = 'org'

/** Split a path or URL into the part before `#` and the fragment (with `#`). */
const splitHash = (url: string): [string, string] => {
  const i = url.indexOf('#')
  return i === -1 ? [url, ''] : [url.slice(0, i), url.slice(i)]
}

/** Add (or replace) `?org=` on a path or absolute URL, keeping any fragment. */
export function withOrg(url: string, orgId?: string | null): string {
  if (!orgId) return url
  const [head, hash] = splitHash(withoutOrg(url))
  const sep = head.includes('?') ? '&' : '?'
  return `${head}${sep}${ORG_PARAM}=${encodeURIComponent(orgId)}${hash}`
}

/** Remove `org=` from a path or absolute URL, keeping other params and the fragment. */
export function withoutOrg(url: string): string {
  const [head, hash] = splitHash(url)
  const q = head.indexOf('?')
  if (q === -1) return url
  const params = new URLSearchParams(head.slice(q + 1))
  if (!params.has(ORG_PARAM)) return url
  params.delete(ORG_PARAM)
  const rest = params.toString()
  return `${head.slice(0, q)}${rest ? `?${rest}` : ''}${hash}`
}

/** The organisation a link names, or null. `search` is `location.search`. */
export function readOrgParam(search: string): string | null {
  return new URLSearchParams(search).get(ORG_PARAM) || null
}
